import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye, AlertTriangle, Clock, Calendar, FileText, ExternalLink,
  Loader2, RefreshCw, Filter, ChevronRight, Search, AlertCircle,
  CheckCircle2, XCircle, Euro, Banknote, PiggyBank, TrendingUp, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../components/ui/popover';
import { expedientesAPI, pagosAPI } from '../api';
import { toast } from '../hooks/use-toast';

const FILTROS_MESES = [
  { value: '1', label: '1 mes sin actividad', descripcion: 'Entre 0 y 30 días' },
  { value: '2', label: '2 meses sin actividad', descripcion: 'Entre 31 y 60 días' },
  { value: '3', label: '3 meses sin actividad', descripcion: 'Entre 61 y 90 días' },
  { value: '4', label: '4 meses sin actividad', descripcion: 'Entre 91 y 120 días' },
  { value: '5', label: 'Superior a 4 meses', descripcion: 'Más de 120 días' },
];

const TIPOS_CONCEPTO_PAGO = [
  { value: 'principal', label: 'Principal de la condena/reclamación', icon: '💰' },
  { value: 'intereses', label: 'Intereses legales y moratorios', icon: '📈' },
  { value: 'costas_primera', label: 'Costas de primera instancia', icon: '⚖️' },
  { value: 'costas_segunda', label: 'Costas de segunda instancia', icon: '⚖️' },
  { value: 'costas_tercera', label: 'Costas de tercera instancia', icon: '⚖️' },
  { value: 'otras_costas', label: 'Otras costas', icon: '📋' },
  { value: 'otros', label: 'Otras cantidades', icon: '📝' },
];

const SupervisionExpedientes = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  
  // General - Estados
  const [loading, setLoading] = useState(true);
  const [loadingResumen, setLoadingResumen] = useState(true);
  const [expedientes, setExpedientes] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [filtroMeses, setFiltroMeses] = useState('1');
  const [total, setTotal] = useState(0);
  
  // Pagos - Estados
  const [loadingPagos, setLoadingPagos] = useState(true);
  const [loadingPagosResumen, setLoadingPagosResumen] = useState(true);
  const [expedientesPagos, setExpedientesPagos] = useState([]);
  const [pagosResumen, setPagosResumen] = useState(null);
  const [totalPagosPendientes, setTotalPagosPendientes] = useState(0);
  const [filtrosTiposPago, setFiltrosTiposPago] = useState([]);  // Filtros de tipos de concepto

  // =============== CARGA DE DATOS - GENERAL ===============
  
  const loadResumen = useCallback(async () => {
    try {
      setLoadingResumen(true);
      const response = await expedientesAPI.getSupervisionResumen();
      setResumen(response.data);
    } catch (error) {
      console.error('Error cargando resumen:', error);
    } finally {
      setLoadingResumen(false);
    }
  }, []);

  const loadExpedientes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await expedientesAPI.getSupervisionSinActividad(parseInt(filtroMeses));
      setExpedientes(response.data.expedientes || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error cargando expedientes:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los expedientes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [filtroMeses]);

  // =============== CARGA DE DATOS - PAGOS ===============
  
  const loadPagosResumen = useCallback(async () => {
    try {
      setLoadingPagosResumen(true);
      const response = await pagosAPI.getPagosResumen();
      setPagosResumen(response.data);
    } catch (error) {
      console.error('Error cargando resumen de pagos:', error);
    } finally {
      setLoadingPagosResumen(false);
    }
  }, []);

  const loadExpedientesPagos = useCallback(async () => {
    try {
      setLoadingPagos(true);
      const response = await pagosAPI.getPagosPendientes(filtrosTiposPago.length > 0 ? filtrosTiposPago : null);
      setExpedientesPagos(response.data.expedientes || []);
      setTotalPagosPendientes(response.data.total_pendiente || 0);
    } catch (error) {
      console.error('Error cargando expedientes con pagos:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los expedientes con pagos pendientes",
        variant: "destructive"
      });
    } finally {
      setLoadingPagos(false);
    }
  }, [filtrosTiposPago]);

  // =============== EFFECTS ===============
  
  useEffect(() => {
    loadResumen();
    loadPagosResumen();
  }, [loadResumen, loadPagosResumen]);

  useEffect(() => {
    loadExpedientes();
  }, [loadExpedientes]);

  useEffect(() => {
    if (activeTab === 'pagos') {
      loadExpedientesPagos();
    }
  }, [activeTab, loadExpedientesPagos]);

  // Recargar cuando cambian los filtros de tipos de pago
  useEffect(() => {
    if (activeTab === 'pagos') {
      loadExpedientesPagos();
    }
  }, [filtrosTiposPago, activeTab, loadExpedientesPagos]);

  // =============== HELPERS ===============

  const handleFiltroChange = (value) => {
    setFiltroMeses(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const getBadgeColor = (dias) => {
    if (dias <= 30) return 'bg-green-100 text-green-700';
    if (dias <= 60) return 'bg-yellow-100 text-yellow-700';
    if (dias <= 90) return 'bg-orange-100 text-orange-700';
    if (dias <= 120) return 'bg-red-100 text-red-700';
    return 'bg-red-200 text-red-800';
  };

  const getDiasLabel = (dias) => {
    if (dias === 1) return '1 día';
    if (dias < 30) return `${dias} días`;
    const meses = Math.floor(dias / 30);
    const diasRestantes = dias % 30;
    if (meses === 1) return diasRestantes > 0 ? `1 mes y ${diasRestantes} días` : '1 mes';
    return diasRestantes > 0 ? `${meses} meses y ${diasRestantes} días` : `${meses} meses`;
  };

  const handleRefresh = () => {
    if (activeTab === 'general') {
      loadResumen();
      loadExpedientes();
    } else {
      loadPagosResumen();
      loadExpedientesPagos();
    }
  };

  const handleToggleTipoPago = (tipo) => {
    setFiltrosTiposPago(prev => {
      if (prev.includes(tipo)) {
        return prev.filter(t => t !== tipo);
      } else {
        return [...prev, tipo];
      }
    });
  };

  const handleClearFiltrosPago = () => {
    setFiltrosTiposPago([]);
  };

  const handleSelectAllFiltrosPago = () => {
    setFiltrosTiposPago(TIPOS_CONCEPTO_PAGO.map(t => t.value));
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Eye className="w-6 h-6 text-blue-600" />
            Supervisión de Expedientes
          </h1>
          <p className="text-gray-600">Seguimiento de expedientes abiertos y pagos pendientes</p>
        </div>
        <Button variant="outline" onClick={handleRefresh}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Sin Actividad
          </TabsTrigger>
          <TabsTrigger value="pagos" className="flex items-center gap-2">
            <Euro className="w-4 h-4" />
            Pagos Pendientes
          </TabsTrigger>
        </TabsList>

        {/* ==================== TAB GENERAL ==================== */}
        <TabsContent value="general" className="space-y-6 mt-6">
          {/* Resumen Cards - General */}
          {!loadingResumen && resumen && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="text-xs text-gray-500">Con actividad</p>
                      <p className="text-xl font-bold text-green-600">{resumen.con_actividad_reciente}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className={`border-l-4 border-l-yellow-500 cursor-pointer hover:bg-yellow-50 transition-colors ${filtroMeses === '1' ? 'ring-2 ring-yellow-400' : ''}`}
                onClick={() => setFiltroMeses('1')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-yellow-500" />
                    <div>
                      <p className="text-xs text-gray-500">1 mes</p>
                      <p className="text-xl font-bold text-yellow-600">{resumen.sin_actividad_1_mes}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className={`border-l-4 border-l-orange-400 cursor-pointer hover:bg-orange-50 transition-colors ${filtroMeses === '2' ? 'ring-2 ring-orange-400' : ''}`}
                onClick={() => setFiltroMeses('2')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-orange-400" />
                    <div>
                      <p className="text-xs text-gray-500">2 meses</p>
                      <p className="text-xl font-bold text-orange-500">{resumen.sin_actividad_2_meses}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className={`border-l-4 border-l-orange-500 cursor-pointer hover:bg-orange-50 transition-colors ${filtroMeses === '3' ? 'ring-2 ring-orange-500' : ''}`}
                onClick={() => setFiltroMeses('3')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-500">3 meses</p>
                      <p className="text-xl font-bold text-orange-600">{resumen.sin_actividad_3_meses}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className={`border-l-4 border-l-red-400 cursor-pointer hover:bg-red-50 transition-colors ${filtroMeses === '4' ? 'ring-2 ring-red-400' : ''}`}
                onClick={() => setFiltroMeses('4')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-400" />
                    <div>
                      <p className="text-xs text-gray-500">4 meses</p>
                      <p className="text-xl font-bold text-red-500">{resumen.sin_actividad_4_meses}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className={`border-l-4 border-l-red-600 cursor-pointer hover:bg-red-50 transition-colors ${filtroMeses === '5' ? 'ring-2 ring-red-600' : ''}`}
                onClick={() => setFiltroMeses('5')}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="text-xs text-gray-500">&gt; 4 meses</p>
                      <p className="text-xl font-bold text-red-700">{resumen.sin_actividad_mas_4_meses}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filtros */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Filtrar por período:</span>
                </div>
                <Select value={filtroMeses} onValueChange={handleFiltroChange}>
                  <SelectTrigger className="w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FILTROS_MESES.map((filtro) => (
                      <SelectItem key={filtro.value} value={filtro.value}>
                        <div>
                          <span>{filtro.label}</span>
                          <span className="text-xs text-gray-400 ml-2">({filtro.descripcion})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="ml-auto">
                  <Badge variant="outline" className="text-base px-4 py-1">
                    {total} {total === 1 ? 'expediente' : 'expedientes'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lista de Expedientes - General */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Expedientes sin Actividad
              </CardTitle>
              <CardDescription>
                {FILTROS_MESES.find(f => f.value === filtroMeses)?.label} - {FILTROS_MESES.find(f => f.value === filtroMeses)?.descripcion}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Cargando expedientes...</span>
                </div>
              ) : expedientes.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-16 h-16 mx-auto text-green-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">¡Excelente!</h3>
                  <p className="text-gray-500">No hay expedientes sin actividad en este período</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expediente</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rama / Fase</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sin Actividad</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Última Actuación</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {expedientes.map((exp) => (
                        <tr key={exp.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <div>
                              <p className="font-medium text-blue-600">{exp.numero_expediente}</p>
                              <p className="text-sm text-gray-600 line-clamp-1 max-w-xs" title={exp.titulo}>
                                {exp.titulo}
                              </p>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-sm">{exp.cliente_nombre || '-'}</span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="space-y-1">
                              <Badge variant="outline" className="text-xs">{exp.rama}</Badge>
                              <p className="text-xs text-gray-500">{exp.fase}</p>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <Badge className={getBadgeColor(exp.dias_sin_actividad)}>
                              {getDiasLabel(exp.dias_sin_actividad)}
                            </Badge>
                          </td>
                          <td className="px-4 py-4">
                            {exp.ultima_actuacion ? (
                              <div className="max-w-xs">
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-3 h-3 text-gray-400" />
                                  <span className="text-xs text-gray-500">
                                    {formatDate(exp.ultima_actuacion.fecha)}
                                  </span>
                                </div>
                                <p className="text-sm font-medium text-gray-700 mt-1">
                                  {exp.ultima_actuacion.titulo || exp.ultima_actuacion.tipo}
                                </p>
                                {exp.ultima_actuacion.descripcion && (
                                  <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                                    {exp.ultima_actuacion.descripcion}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400 italic">Sin actuaciones</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/expedientes/${exp.id}`)}
                              className="gap-1"
                            >
                              <ExternalLink className="w-4 h-4" />
                              Ver
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== TAB PAGOS ==================== */}
        <TabsContent value="pagos" className="space-y-6 mt-6">
          {/* Resumen Cards - Pagos */}
          {!loadingPagosResumen && pagosResumen && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Con pagos configurados</p>
                      <p className="text-2xl font-bold text-blue-600">{pagosResumen.expedientes_con_pagos_configurados}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-orange-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Con pagos pendientes</p>
                      <p className="text-2xl font-bold text-orange-600">{pagosResumen.expedientes_con_pendientes}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Todo pagado</p>
                      <p className="text-2xl font-bold text-green-600">{pagosResumen.expedientes_todo_pagado}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-red-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <Euro className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total pendiente</p>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(pagosResumen.total_pendiente)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Resumen financiero general */}
          {!loadingPagosResumen && pagosResumen && pagosResumen.total_a_cobrar > 0 && (
            <Card className="bg-gradient-to-r from-blue-50 to-green-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <PiggyBank className="w-8 h-8 text-blue-600" />
                    <div>
                      <p className="text-sm text-gray-600">Progreso global de cobros</p>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-lg font-bold text-green-600">
                          {formatCurrency(pagosResumen.total_pagado)}
                        </span>
                        <span className="text-gray-400">/</span>
                        <span className="text-lg font-bold text-blue-600">
                          {formatCurrency(pagosResumen.total_a_cobrar)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <Progress 
                      value={pagosResumen.total_a_cobrar > 0 ? (pagosResumen.total_pagado / pagosResumen.total_a_cobrar * 100) : 0} 
                      className="w-32 h-3"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      {Math.round((pagosResumen.total_pagado / pagosResumen.total_a_cobrar * 100) || 0)}% cobrado
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filtros de Tipos de Concepto */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Filtrar por tipo de concepto:</span>
                </div>
                
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="min-w-[200px] justify-between">
                      {filtrosTiposPago.length === 0 ? (
                        <span className="text-gray-500">Todos los conceptos</span>
                      ) : filtrosTiposPago.length === TIPOS_CONCEPTO_PAGO.length ? (
                        <span>Todos seleccionados</span>
                      ) : (
                        <span>{filtrosTiposPago.length} seleccionado(s)</span>
                      )}
                      <ChevronRight className="w-4 h-4 ml-2 rotate-90" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <div className="p-3 border-b bg-gray-50">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">Tipos de Concepto</span>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={handleSelectAllFiltrosPago}>
                            Todos
                          </Button>
                          <Button variant="ghost" size="sm" onClick={handleClearFiltrosPago}>
                            Ninguno
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="p-2 max-h-64 overflow-y-auto">
                      {TIPOS_CONCEPTO_PAGO.map((tipo) => (
                        <div
                          key={tipo.value}
                          className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                          onClick={() => handleToggleTipoPago(tipo.value)}
                        >
                          <Checkbox
                            checked={filtrosTiposPago.includes(tipo.value)}
                            onCheckedChange={() => handleToggleTipoPago(tipo.value)}
                          />
                          <span className="text-lg">{tipo.icon}</span>
                          <span className="text-sm">{tipo.label}</span>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Badges de filtros activos */}
                {filtrosTiposPago.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {filtrosTiposPago.map((tipo) => {
                      const tipoInfo = TIPOS_CONCEPTO_PAGO.find(t => t.value === tipo);
                      return (
                        <Badge 
                          key={tipo} 
                          variant="secondary"
                          className="flex items-center gap-1 pr-1"
                        >
                          <span>{tipoInfo?.icon}</span>
                          <span className="max-w-[100px] truncate">{tipoInfo?.label}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-4 w-4 p-0 hover:bg-gray-300 rounded-full ml-1"
                            onClick={() => handleToggleTipoPago(tipo)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </Badge>
                      );
                    })}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearFiltrosPago}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      Limpiar filtros
                    </Button>
                  </div>
                )}

                <div className="ml-auto">
                  <Badge variant="outline" className="text-base px-4 py-1">
                    {expedientesPagos.length} {expedientesPagos.length === 1 ? 'expediente' : 'expedientes'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lista de Expedientes - Pagos Pendientes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Banknote className="w-5 h-5" />
                Expedientes con Pagos Pendientes
              </CardTitle>
              <CardDescription>
                Listado de expedientes con importes pendientes de cobro
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingPagos ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Cargando expedientes...</span>
                </div>
              ) : expedientesPagos.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-16 h-16 mx-auto text-green-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">¡Excelente!</h3>
                  <p className="text-gray-500">No hay expedientes con pagos pendientes</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expediente</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rama / Fase</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total a Cobrar</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pagado</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pendiente</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Progreso</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {expedientesPagos.map((exp) => (
                        <tr key={exp.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <div>
                              <p className="font-medium text-blue-600">{exp.numero_expediente}</p>
                              <p className="text-sm text-gray-600 line-clamp-1 max-w-xs" title={exp.titulo}>
                                {exp.titulo}
                              </p>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-sm">{exp.cliente_nombre || '-'}</span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="space-y-1">
                              <Badge variant="outline" className="text-xs">{exp.rama}</Badge>
                              <p className="text-xs text-gray-500">{exp.fase}</p>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <span className="font-medium text-blue-600">{formatCurrency(exp.total_a_cobrar)}</span>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <span className="font-medium text-green-600">{formatCurrency(exp.total_pagado)}</span>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <Badge className="bg-orange-100 text-orange-700">
                              {formatCurrency(exp.total_pendiente)}
                            </Badge>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2 justify-center">
                              <Progress value={exp.porcentaje_pagado} className="w-16 h-2" />
                              <span className="text-xs text-gray-500">{exp.porcentaje_pagado}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/expedientes/${exp.id}`)}
                              className="gap-1"
                            >
                              <ExternalLink className="w-4 h-4" />
                              Ver
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Información */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900">Información sobre la supervisión</h4>
              <ul className="text-sm text-blue-800 mt-2 space-y-1 list-disc list-inside">
                <li>Se consideran solo expedientes con estado <strong>activo</strong></li>
                <li><strong>Sin Actividad:</strong> Mide el tiempo desde la última actuación registrada</li>
                <li><strong>Pagos Pendientes:</strong> Muestra expedientes con conceptos de pago configurados y saldo pendiente</li>
                <li>Haz clic en "Ver" para acceder directamente al expediente</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SupervisionExpedientes;
